import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Select, Modal, Switch } from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type {
  WizardStep,
  KeyFormat,
  DetectedColumn,
  KeyMapping,
  AnonymisationJob,
  LibraryStandardOption,
} from './anonymisation.service';
import {
  detectPiiColumns,
  parseUploadedFile,
  generateKeyMapping,
  buildAnonymisedRows,
  downloadKeyMappingCSV,
  downloadAnonymisedCSV,
  getKeyExpiryLabel,
  buildKeyMappingBlob,
  buildAnonymisedBlob,
  resolveKeyFromName,
  fetchLibraryStandards,
  saveAnonymisedFile,
  createAnonymisationJob,
  listAnonymisationJobs,
  reexportKeyCSV,
} from './anonymisation.service';
import './DataAnonymisationPage.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = '.csv,.xlsx,.xls';
const MAX_FILE_MB = 50;

// ── Component ─────────────────────────────────────────────────────────────────

const DataAnonymisationPage: React.FC = () => {
  const { t } = useTranslation('dataAnonymisation');
  const { id: projectId } = useParams<{ id: string }>();

  const KEY_FORMAT_OPTIONS: { value: KeyFormat; label: string; preview: string }[] = [
    { value: 'RESP', label: 'RESP_001', preview: 'RESP_001, RESP_002, …' },
    { value: 'CODE', label: 'CODE_001', preview: 'CODE_001, CODE_002, …' },
    { value: 'ID', label: 'ID_001', preview: 'ID_001, ID_002, …' },
    { value: 'CUSTOM', label: t('configure.customLabel'), preview: t('configure.customPreviewHint') },
  ];

  const WIZARD_STEPS: { key: WizardStep; label: string }[] = [
    { key: 'upload', label: t('steps.upload') },
    { key: 'configure', label: t('steps.configure') },
    { key: 'preview', label: t('steps.preview') },
    { key: 'export', label: t('steps.export') },
  ];

  // ── Tabs
  const [activeTab, setActiveTab] = useState<'anonymise' | 'history'>('anonymise');

  // ── Wizard
  const [step, setStep] = useState<WizardStep>('upload');

  // Step 1 — Upload
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState('');
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<Record<string, string>[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2 — Configure
  const [selectedStandard, setSelectedStandard] = useState<string | null>(null);
  const [columns, setColumns] = useState<DetectedColumn[]>([]);
  const [keyFormat, setKeyFormat] = useState<KeyFormat>('RESP');
  const [customPrefix, setCustomPrefix] = useState('');

  // Step 3/4 — Preview + Export
  const [keyMapping, setKeyMapping] = useState<KeyMapping[]>([]);
  const [anonymisedRows, setAnonymisedRows] = useState<Record<string, string>[]>([]);
  const [keyDownloaded, setKeyDownloaded] = useState(false);
  const [fileSaved, setFileSaved] = useState(false);
  const [anonymisedDownloaded, setAnonymisedDownloaded] = useState(false);

  // ── History
  const [history, setHistory] = useState<AnonymisationJob[]>([]);

  // ── API loading states
  const [standards, setStandards] = useState<LibraryStandardOption[]>([]);
  const [standardsLoading, setStandardsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!projectId) return;
    setStandardsLoading(true);
    fetchLibraryStandards(projectId)
      .then(setStandards)
      .catch(() => toast.error(t('toasts.loadStandardsFailed')))
      .finally(() => setStandardsLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setHistoryLoading(true);
    listAnonymisationJobs(projectId)
      .then(setHistory)
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [projectId]);

  const resolvedPrefix = keyFormat === 'CUSTOM'
    ? (customPrefix.trim().toUpperCase() || 'KEY')
    : keyFormat;

  const selectedColumns = columns.filter(c => c.selected);
  const canProceedFromConfigure =
    selectedStandard !== null && selectedColumns.length > 0;

  const resetWizard = () => {
    setStep('upload');
    setUploadedFile(null);
    setParseError('');
    setParsedHeaders([]);
    setParsedRows([]);
    setSelectedStandard(null);
    setColumns([]);
    setKeyFormat('RESP');
    setCustomPrefix('');
    setKeyMapping([]);
    setAnonymisedRows([]);
    setKeyDownloaded(false);
    setFileSaved(false);
  };

  // ── File handling ─────────────────────────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    setParseError('');

    const lower = file.name.toLowerCase();
    const validExt = ['.csv', '.xlsx', '.xls'].some(ext => lower.endsWith(ext));
    if (!validExt) {
      setParseError(t('errors.unsupportedFileType'));
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setParseError(t('errors.fileTooLarge', { maxMb: MAX_FILE_MB }));
      return;
    }

    try {
      const { headers, rows } = await parseUploadedFile(file);
      setUploadedFile(file);
      setParsedHeaders(headers);
      setParsedRows(rows);
    } catch (err: any) {
      setParseError(err.message ?? t('errors.parseFailed'));
    }
  }, [t]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  // ── Standard selection ────────────────────────────────────────────────────

  const handleStandardChange = (standardId: string) => {
    setSelectedStandard(standardId);
    const standard = standards.find(s => s.id === standardId);
    const keySlug = standard ? resolveKeyFromName(standard.name) : standardId;
    const detected = detectPiiColumns(parsedHeaders, keySlug);
    setColumns(detected);
  };

  const toggleColumn = (columnName: string) => {
    setColumns(prev =>
      prev.map(c => (c.column === columnName ? { ...c, selected: !c.selected } : c)),
    );
  };

  // ── Build preview & anonymised data ──────────────────────────────────────

  const buildPreview = () => {
    const mapping = generateKeyMapping(parsedRows, columns, resolvedPrefix);
    const anonRows = buildAnonymisedRows(parsedRows, mapping);
    setKeyMapping(mapping);
    setAnonymisedRows(anonRows);
    setStep('preview');
  };

  // ── Export actions ────────────────────────────────────────────────────────

  const handleDownloadKey = () => {
    if (!uploadedFile) return;
    downloadKeyMappingCSV(keyMapping, uploadedFile.name);
    setKeyDownloaded(true);
    toast.success(t('toasts.keyDownloaded'));
  };

  const handleDownloadAnonymised = () => {
    if (!uploadedFile) return;
    downloadAnonymisedCSV(parsedHeaders, anonymisedRows, uploadedFile.name);
    setAnonymisedDownloaded(true);
    toast.success(t('toasts.anonymisedDownloaded'));
  };

  const handleSaveToProject = async () => {
    if (!uploadedFile || !projectId) return;
    setIsSaving(true);
    try {
      const baseName = uploadedFile.name.replace(/\.[^/.]+$/, '');
      const today = new Date().toISOString().slice(0, 10);
      const anonFileName = `${baseName}_anonymised_${today}.csv`;

      // Group 4 — upload anonymised file to project
      const anonBlob = buildAnonymisedBlob(parsedHeaders, anonymisedRows);
      const anonFile = new File([anonBlob], anonFileName, { type: 'text/csv' });
      await saveAnonymisedFile(projectId, anonFile, uploadedFile.name);

      // Group 2+3 — create job record + upload key mapping for 30-day re-export
      const standardName = standards.find(s => s.id === selectedStandard)?.name ?? selectedStandard ?? '';
      const keyBlob = buildKeyMappingBlob(keyMapping);
      const keyFile = new File([keyBlob], `${baseName}_key_mapping.csv`, { type: 'text/csv' });
      const { jobId, keyAvailableUntil } = await createAnonymisationJob(projectId, {
        fileName: anonFileName,
        standardUsed: standardName,
        columnsAnonymised: selectedColumns.length,
        valuesAnonymised: keyMapping.length,
        keyFormat: resolvedPrefix,
        keyFile,
      });

      const job: AnonymisationJob = {
        id: jobId,
        fileName: anonFileName,
        standardUsed: standardName,
        columnsAnonymised: selectedColumns.length,
        valuesAnonymised: keyMapping.length,
        createdAt: new Date().toISOString(),
        keyAvailableUntil,
      };
      setHistory(prev => [job, ...prev]);
      setFileSaved(true);
      toast.success(t('toasts.savedToProject'));
    } catch {
      toast.error(t('toasts.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render: Step indicator ────────────────────────────────────────────────

  const renderStepper = () => {
    const currentIndex = WIZARD_STEPS.findIndex(s => s.key === step);
    return (
      <div className="da-stepper">
        {WIZARD_STEPS.map((s, i) => {
          const isCompleted = i < currentIndex;
          const isActive = i === currentIndex;
          return (
            <React.Fragment key={s.key}>
              <div className={`da-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                <div className="da-step-circle">
                  {isCompleted ? <CheckCircleOutlined /> : i + 1}
                </div>
                <span className="da-step-label">{s.label}</span>
              </div>
              {i < WIZARD_STEPS.length - 1 && (
                <div className={`da-step-line ${isCompleted ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // ── Render: Step 1 — Upload ───────────────────────────────────────────────

  const renderUpload = () => (
    <div className="da-wizard-content">
      <h3 className="da-step-heading">{t('upload.heading')}</h3>
      <p className="da-step-desc">
        {t('upload.description')}
      </p>

      <div
        className={`da-dropzone ${isDragging ? 'dragging' : ''} ${parseError ? 'error' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className="da-dropzone-icon">
          <UploadOutlined />
        </div>
        {uploadedFile ? (
          <>
            <p className="da-dropzone-filename">{uploadedFile.name}</p>
            <p className="da-dropzone-meta">
              {t('upload.columnsRowsCount', { columns: parsedHeaders.length, rows: parsedRows.length })}
            </p>
            <p className="da-dropzone-replace">{t('upload.clickOrDragReplace')}</p>
          </>
        ) : (
          <>
            <p className="da-dropzone-text">{t('upload.dragDropOrBrowse')}</p>
            <p className="da-dropzone-meta">{t('upload.acceptedTypesMeta', { maxMb: MAX_FILE_MB })}</p>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      {parseError && (
        <div className="da-error-msg">
          <WarningOutlined /> {parseError}
        </div>
      )}

      <div className="da-wizard-footer da-wizard-footer--center">
        <button
          className="da-btn-primary"
          disabled={!uploadedFile || !!parseError}
          onClick={() => setStep('configure')}
        >
          {t('upload.next')}
        </button>
      </div>
    </div>
  );

  // ── Render: Step 2 — Configure ────────────────────────────────────────────

  const renderConfigure = () => {
    const piiColumns = columns.filter(c => c.piiType !== 'Non-PII');
    const nonPiiColumns = columns.filter(c => c.piiType === 'Non-PII');

    return (
      <div className="da-wizard-content">
        <h3 className="da-step-heading">{t('configure.heading')}</h3>
        <p className="da-step-desc">
          {t('configure.description')}
        </p>

        {/* Standard selector */}
        <div className="da-config-section">
          <p className="da-config-section-title">{t('configure.libraryStandard')}</p>
          <Select
            placeholder={t('configure.standardPlaceholder')}
            value={selectedStandard}
            onChange={handleStandardChange}
            loading={standardsLoading}
            style={{ width: '100%' }}
            options={standards.map(s => ({ value: s.id, label: s.name }))}
            notFoundContent={
              standardsLoading
                ? t('configure.loadingStandards')
                : t('configure.noStandardsFound')
            }
          />
          {selectedStandard && (
            <p className="da-config-standard-hint">
              <InfoCircleOutlined />{' '}
              {standards.find(s => s.id === selectedStandard)?.description}
            </p>
          )}
        </div>

        {/* Column review */}
        {selectedStandard && (
          <>
            <div className="da-config-section">
              <p className="da-config-section-title">
                {t('configure.detectedPiiColumns')}
                <span className="da-config-badge da-config-badge--pii">
                  {t('configure.detectedCount', { count: piiColumns.length })}
                </span>
              </p>

              {piiColumns.length === 0 ? (
                <div className="da-no-detection">
                  {t('configure.noDetection')}
                </div>
              ) : (
                <table className="da-columns-table">
                  <thead>
                    <tr>
                      <th>{t('configure.columnName')}</th>
                      <th>{t('configure.piiType')}</th>
                      <th className="da-col-center">{t('configure.anonymise')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {piiColumns.map(col => (
                      <tr key={col.column} className={col.selected ? 'da-col-row--selected' : ''}>
                        <td className="da-col-name">{col.column}</td>
                        <td>
                          <span className="da-pii-badge">{col.piiType}</span>
                        </td>
                        <td className="da-col-center">
                          <Switch
                            checked={col.selected}
                            onChange={() => toggleColumn(col.column)}
                            size="small"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Non-PII columns — manual inclusion */}
            {nonPiiColumns.length > 0 && (
              <div className="da-config-section">
                <p className="da-config-section-title">
                  {t('configure.otherColumns')}
                  <span className="da-config-hint-inline">
                    {t('configure.otherColumnsHint')}
                  </span>
                </p>
                <table className="da-columns-table da-columns-table--muted">
                  <thead>
                    <tr>
                      <th>{t('configure.columnName')}</th>
                      <th>{t('configure.status')}</th>
                      <th className="da-col-center">{t('configure.include')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nonPiiColumns.map(col => (
                      <tr key={col.column}>
                        <td className="da-col-name">{col.column}</td>
                        <td className="da-col-muted">{t('configure.notDetected')}</td>
                        <td className="da-col-center">
                          <Switch
                            checked={col.selected}
                            onChange={() => toggleColumn(col.column)}
                            size="small"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Key format */}
            <div className="da-config-section">
              <p className="da-config-section-title">{t('configure.keyFormat')}</p>
              <div className="da-key-format-options">
                {KEY_FORMAT_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`da-key-format-item ${keyFormat === opt.value ? 'selected' : ''}`}
                    onClick={() => setKeyFormat(opt.value)}
                  >
                    <div className="da-key-format-radio">
                      <div className={`da-radio-dot ${keyFormat === opt.value ? 'active' : ''}`} />
                    </div>
                    <div>
                      <p className="da-key-format-label">{opt.label}</p>
                      <p className="da-key-format-preview">{opt.preview}</p>
                    </div>
                  </label>
                ))}
              </div>
              {keyFormat === 'CUSTOM' && (
                <div className="da-custom-prefix-wrap">
                  <input
                    className="da-custom-prefix-input"
                    placeholder={t('configure.customPrefixPlaceholder')}
                    value={customPrefix}
                    maxLength={20}
                    onChange={e =>
                      setCustomPrefix(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toUpperCase())
                    }
                  />
                  {customPrefix && (
                    <span className="da-custom-prefix-live">
                      {t('configure.customPrefixPreview', { prefix: customPrefix })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="da-wizard-footer">
          <button className="da-btn-secondary" onClick={() => setStep('upload')}>
            {t('configure.back')}
          </button>
          <button
            className="da-btn-primary"
            disabled={!canProceedFromConfigure}
            onClick={buildPreview}
          >
            {t('configure.next')}
          </button>
        </div>
      </div>
    );
  };

  // ── Render: Step 3 — Preview ──────────────────────────────────────────────

  const renderPreview = () => {
    const previewMapping = keyMapping.slice(0, 50);
    const truncated = keyMapping.length > 50;

    return (
      <div className="da-wizard-content">
        <h3 className="da-step-heading">{t('preview.heading')}</h3>
        <p className="da-step-desc">
          {t('preview.description')}
        </p>

        {/* Summary cards */}
        <div className="da-preview-stats">
          <div className="da-preview-stat">
            <span className="da-preview-stat-value">{selectedColumns.length}</span>
            <span className="da-preview-stat-label">{t('preview.columnsToAnonymise')}</span>
          </div>
          <div className="da-preview-stat">
            <span className="da-preview-stat-value">{keyMapping.length}</span>
            <span className="da-preview-stat-label">{t('preview.uniqueValues')}</span>
          </div>
          <div className="da-preview-stat">
            <span className="da-preview-stat-value">{parsedRows.length}</span>
            <span className="da-preview-stat-label">{t('preview.totalRows')}</span>
          </div>
        </div>

        {/* Columns being anonymised */}
        <div className="da-preview-columns">
          {selectedColumns.map(col => (
            <span key={col.column} className="da-preview-col-tag">
              {col.column}
              <span className="da-preview-col-type"> · {col.piiType}</span>
            </span>
          ))}
        </div>

        {/* Key mapping table */}
        <div className="da-preview-table-wrap">
          <table className="da-preview-table">
            <thead>
              <tr>
                <th>{t('preview.key')}</th>
                <th>{t('preview.originalValue')}</th>
                <th>{t('configure.piiType')}</th>
                <th>{t('preview.column')}</th>
              </tr>
            </thead>
            <tbody>
              {previewMapping.map((m, i) => (
                <tr key={i}>
                  <td><span className="da-key-chip">{m.key}</span></td>
                  <td className="da-original-val">{m.originalValue}</td>
                  <td><span className="da-pii-badge">{m.type}</span></td>
                  <td className="da-col-muted">{m.column}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {truncated && (
            <p className="da-preview-truncated">
              {t('preview.truncated', { total: keyMapping.length })}
            </p>
          )}
        </div>

        <div className="da-wizard-footer">
          <button className="da-btn-secondary" onClick={() => setStep('configure')}>
            {t('preview.back')}
          </button>
          <button className="da-btn-primary" onClick={() => setStep('export')}>
            {t('preview.next')}
          </button>
        </div>
      </div>
    );
  };

  // ── Render: Step 4 — Export ───────────────────────────────────────────────

  const renderExport = () => {
    const allDone = keyDownloaded && fileSaved;

    return (
      <div className="da-wizard-content">
        <h3 className="da-step-heading">{t('export.heading')}</h3>
        <p className="da-step-desc">
          {t('export.description')}
        </p>

        <div className="da-export-actions">
          {/* Key mapping */}
          <div className={`da-export-card ${keyDownloaded ? 'done' : ''}`}>
            <div className="da-export-card-header">
              <div className="da-export-card-icon">
                {keyDownloaded ? <CheckCircleOutlined /> : <DownloadOutlined />}
              </div>
              <div>
                <p className="da-export-card-title">{t('export.downloadKeyTitle')}</p>
                <p className="da-export-card-sub">
                  {t('export.downloadKeySub')}
                </p>
              </div>
            </div>
            <div className="da-export-warning">
              <WarningOutlined />
              <span>
                {t('export.keyWarning')}
              </span>
            </div>
            <button
              className={`da-btn-primary da-export-btn ${keyDownloaded ? 'da-btn-done' : ''}`}
              onClick={handleDownloadKey}
            >
              <DownloadOutlined />
              {keyDownloaded ? t('export.downloaded') : t('export.downloadKeyButton')}
            </button>
          </div>

          {/* Save to project */}
          <div className={`da-export-card ${fileSaved ? 'done' : ''}`}>
            <div className="da-export-card-header">
              <div className="da-export-card-icon">
                {fileSaved ? <CheckCircleOutlined /> : <UploadOutlined />}
              </div>
              <div>
                <p className="da-export-card-title">{t('export.saveToProjectTitle')}</p>
                <p className="da-export-card-sub">
                  {t('export.saveToProjectSub')}
                </p>
              </div>
            </div>
            <button
              className={`da-btn-primary da-export-btn ${fileSaved ? 'da-btn-done' : ''}`}
              onClick={handleSaveToProject}
              disabled={isSaving || fileSaved}
            >
              {isSaving ? t('export.saving') : fileSaved ? t('export.savedToProject') : t('export.saveToProjectButton')}
            </button>
            <button
              className={`da-btn-secondary da-export-btn ${anonymisedDownloaded ? 'da-btn-done' : ''}`}
              onClick={handleDownloadAnonymised}
              style={{ marginTop: 8 }}
            >
              <DownloadOutlined />
              {anonymisedDownloaded ? t('export.downloaded') : t('export.downloadAnonymisedButton')}
            </button>
          </div>
        </div>

        {allDone && (
          <div className="da-export-complete">
            <CheckCircleOutlined className="da-export-complete-icon" />
            <div>
              <p className="da-export-complete-title">{t('export.completeTitle')}</p>
              <p className="da-export-complete-sub">
                {t('export.completeSub')}
              </p>
            </div>
          </div>
        )}

        <div className="da-wizard-footer">
          <button className="da-btn-secondary" onClick={() => setStep('preview')}>
            {t('export.back')}
          </button>
          <button
            className="da-btn-outline"
            onClick={() => {
              if (!allDone) {
                Modal.confirm({
                  title: t('export.leaveConfirm.title'),
                  content: t('export.leaveConfirm.content'),
                  okText: t('export.leaveConfirm.okText'),
                  okType: 'danger',
                  cancelText: t('export.leaveConfirm.cancelText'),
                  onOk: resetWizard,
                });
              } else {
                resetWizard();
              }
            }}
          >
            <ReloadOutlined /> {t('export.startNewJob')}
          </button>
        </div>
      </div>
    );
  };

  // ── Render: History tab ───────────────────────────────────────────────────

  const renderHistory = () => {
    if (historyLoading) {
      return (
        <div className="da-history-empty">
          <p className="da-history-empty-sub">{t('history.loading')}</p>
        </div>
      );
    }
    if (history.length === 0) {
      return (
        <div className="da-history-empty">
          <div className="da-history-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h4 className="da-history-empty-title">{t('history.emptyTitle')}</h4>
          <p className="da-history-empty-sub">
            {t('history.emptySub')}
          </p>
          <button className="da-btn-primary" onClick={() => setActiveTab('anonymise')}>
            {t('history.startAnonymising')}
          </button>
        </div>
      );
    }

    return (
      <div className="da-history-table-wrap">
        <table className="da-history-table">
          <thead>
            <tr>
              <th>{t('history.fileName')}</th>
              <th>{t('history.standardUsed')}</th>
              <th>{t('history.date')}</th>
              <th>{t('history.columns')}</th>
              <th>{t('history.valuesAnonymised')}</th>
              <th>{t('history.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {history.map(job => {
              const { expired, label } = getKeyExpiryLabel(job.keyAvailableUntil);
              return (
                <tr key={job.id}>
                  <td className="da-history-filename">{job.fileName}</td>
                  <td>{job.standardUsed}</td>
                  <td className="da-col-muted">{dayjs(job.createdAt).format('MMM D, YYYY')}</td>
                  <td className="da-col-center">{job.columnsAnonymised}</td>
                  <td className="da-col-center">{job.valuesAnonymised}</td>
                  <td>
                    <div className="da-history-actions">
                      {!expired ? (
                        <button
                          className="da-btn-outline da-btn-sm"
                          onClick={() =>
                            reexportKeyCSV(projectId!, job.id, job.fileName)
                              .then(() => toast.success(t('toasts.reexportSuccess')))
                              .catch(() =>
                                toast.error(t('toasts.reexportFailed')),
                              )
                          }
                          title={label}
                        >
                          <DownloadOutlined /> {t('history.reexportKey')}
                        </button>
                      ) : (
                        <span className="da-key-expired" title={t('history.keyExpiredTooltip')}>
                          {t('history.keyExpired')}
                        </span>
                      )}
                      <span className={`da-expiry-label ${expired ? 'expired' : ''}`}>
                        {label}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="da-page">
      <div className="da-card">

        {/* Header */}
        <div className="da-card-header">
          <div>
            <h1 className="da-page-title">{t('page.title')}</h1>
            <p className="da-page-subtitle">
              {t('page.subtitle')}
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="da-tab-bar">
          <button
            className={`da-tab-btn ${activeTab === 'anonymise' ? 'active' : ''}`}
            onClick={() => setActiveTab('anonymise')}
          >
            {t('tabs.anonymise')}
          </button>
          <button
            className={`da-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            {t('tabs.history')}{history.length > 0 ? ` (${history.length})` : ''}
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'anonymise' && (
          <div className="da-tab-content">
            {renderStepper()}
            {step === 'upload' && renderUpload()}
            {step === 'configure' && renderConfigure()}
            {step === 'preview' && renderPreview()}
            {step === 'export' && renderExport()}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="da-tab-content">
            {renderHistory()}
          </div>
        )}

      </div>
    </div>
  );
};

export default DataAnonymisationPage;
